import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AuthProvider } from "@/hooks/useAuth";
import { StartupProvider } from "@/hooks/useStartupContext";
import { useAuth } from "@/hooks/useAuth.hook";
import { StartupOnboardingModal, PendingInvitesBanner } from "@/components/molecules";
import { AppHeader } from "@/components/layout/AppHeader";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { ProtectedRoute, PublicRoute } from "@/routes/AuthRoutes";

// Pages
import { LandingPage } from "@/components/pages/LandingPage";
import { SignInPage } from "@/components/pages/SignInPage";
import { SignUpPage } from "@/components/pages/SignUpPage";
import { SettingsLayout } from "@/components/pages/SettingsLayout";
import {
  SettingsApiSection,
  SettingsBillingSection,
  SettingsIndexRedirect,
  SettingsInvestorSection,
  SettingsStartupSection,
  SettingsTeamSection,
  SettingsTimelineSection,
} from "@/components/pages/SettingsSections";
import {
  HelpArticleSection,
  HelpIndexRedirect,
  HelpLayout,
} from "@/components/pages/HelpLayout";

// Chart components
import BurndownChartDemo from "@/components/charts/BurndownChartDemo";
import { InvestorPortalPage } from "@/components/pages/InvestorPortalPage";
import { BusinessPlanPage } from "@/components/pages/BusinessPlanPage";
import { AnalyticsBootstrap } from "@/components/analytics/AnalyticsBootstrap";
import { AnalyticsPageTracker } from "@/components/analytics/AnalyticsPageTracker";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
      retry: 1,
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnMount: false, // Don't refetch on component mount if data is fresh
    },
  },
});

// Global modals component
function GlobalModals() {
  const {
    showStartupOnboarding,
    createStartupForNewUser,
    skipStartupSetup,
    loading,
  } = useAuth();

  return (
    <StartupOnboardingModal
      isOpen={showStartupOnboarding}
      onCreateStartup={createStartupForNewUser}
      onSkip={skipStartupSetup}
      isLoading={loading}
    />
  );
}

// Placeholder Dashboard component
function Dashboard() {
  const { userProfile, signOut } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen flex-col bg-page text-white">
      <AppHeader
        displayName={userProfile?.displayName}
        onSettingsClick={() => navigate("/settings/startup")}
        onSignOut={signOut}
      />
      <PendingInvitesBanner />
      <main className="w-full flex-1">
        <BurndownChartDemo />
      </main>
      <SiteFooter variant="compact" />
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AnalyticsBootstrap />
        <Router>
          <AnalyticsPageTracker />
          <Routes>
            {/* Public routes */}
            <Route
              path="/"
              element={
                <PublicRoute home>
                  <LandingPage />
                </PublicRoute>
              }
            />
            <Route
              path="/signin"
              element={
                <PublicRoute>
                  <SignInPage />
                </PublicRoute>
              }
            />
            <Route
              path="/signup"
              element={
                <PublicRoute>
                  <SignUpPage />
                </PublicRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <StartupProvider>
                    <Dashboard />
                  </StartupProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <StartupProvider>
                    <SettingsLayout />
                  </StartupProvider>
                </ProtectedRoute>
              }
            >
              <Route index element={<SettingsIndexRedirect />} />
              <Route path="startup" element={<SettingsStartupSection />} />
              <Route path="timeline" element={<SettingsTimelineSection />} />
              <Route path="team" element={<SettingsTeamSection />} />
              <Route path="investor" element={<SettingsInvestorSection />} />
              <Route path="billing" element={<SettingsBillingSection />} />
              <Route path="api" element={<SettingsApiSection />} />
              <Route path="*" element={<SettingsIndexRedirect />} />
            </Route>

            <Route path="/help" element={<HelpLayout />}>
              <Route index element={<HelpIndexRedirect />} />
              <Route path=":sectionId" element={<HelpArticleSection />} />
            </Route>

            <Route
              path="/business-plan"
              element={
                <ProtectedRoute>
                  <StartupProvider>
                    <BusinessPlanPage />
                  </StartupProvider>
                </ProtectedRoute>
              }
            />

            <Route path="/investor/:token" element={<InvestorPortalPage />} />

            {/* Development routes */}
            <Route path="/chart-demo" element={
              <StartupProvider>
                <BurndownChartDemo />
              </StartupProvider>
            } />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
        
        {/* Global Modals - needs to be inside AuthProvider to access auth state */}
        <GlobalModals />
      </AuthProvider>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
    </QueryClientProvider>
  );
}



export default App;
